import { useState } from "react";
import { colors, spacing, typography } from "@tl4k/frontend-shared";
import { LegalPagesViewer, type LegalPageId } from "./legal/LegalPagesViewer";

export interface TL4KAppFooterProps {
  /** Nome visualizzato dell'applicazione (es. "WordCloud", "Disegna") */
  appLabel: string;
  /** URL pagina accessibilità: se fornito, link SPA; altrimenti viewer a schermo intero */
  accessibilityUrl?: string;
  /** URL pagina privacy: se fornito, link SPA; altrimenti viewer a schermo intero */
  privacyUrl?: string;
  /** URL pagina Open Source / licenze: se fornito, link SPA; altrimenti viewer a schermo intero */
  openSourceUrl?: string;
  /** URL pagina dati societari: se fornito, link SPA; altrimenti viewer a schermo intero (usato dal copyright) */
  companyUrl?: string;
}

/**
 * Footer standard per tutte le applicazioni TL4K.
 *
 * OBBLIGATORIO in ogni app — contiene link normativi:
 * - Dichiarazione di accessibilità (Legge Stanca L. 4/2004, DM 166/2025)
 * - Privacy & Cookie Policy (GDPR, AI Act)
 * - Open Source e trasparenza (licenze, disclosure IA)
 * - Copyright TL4K → dati societari (pagina "La Società")
 *
 * Se l'app fornisce un URL (rotta dedicata) il link è un anchor SPA;
 * altrimenti si apre il viewer a schermo intero (`LegalPagesViewer`),
 * così le pagine sono raggiungibili da TUTTE le app standalone.
 *
 * NON deve contenere controlli auth (quelli stanno in TL4KAppHeader).
 * Altezza massima: 40px. Contrasto ≥4.5:1 (WCAG AA).
 */
export function TL4KAppFooter({
  appLabel,
  accessibilityUrl,
  privacyUrl,
  openSourceUrl,
  companyUrl,
}: TL4KAppFooterProps) {
  const year = new Date().getFullYear();
  const [viewerPage, setViewerPage] = useState<LegalPageId | null>(null);

  const closeViewer = (): void => setViewerPage(null);

  const navStyle: React.CSSProperties = {
    color: colors.info,
    textDecoration: "underline",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "inherit",
  };

  return (
    <>
      <footer
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: `${spacing.sm} ${spacing.lg}`,
          backgroundColor: colors.neutral[100],
          borderTop: `1px solid ${colors.neutral[300]}`,
          fontSize: typography["11-14"].small,
          color: colors.neutral[700],
          minHeight: "40px",
        }}
        role="contentinfo"
        aria-label={`Footer ${appLabel}`}
      >
        <span>
          © {year}{" "}
          {companyUrl ? (
            <a
              href={companyUrl}
              style={{ color: "inherit", textDecoration: "underline" }}
              aria-label="TechLAB4Kids — dati societari"
            >
              TechLab4Kids
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setViewerPage("company")}
              style={{ ...navStyle, color: "inherit" }}
              aria-label="TechLAB4Kids — dati societari"
              aria-haspopup="dialog"
            >
              TechLab4Kids
            </button>
          )}{" "}
          — {appLabel}
        </span>

        <nav style={{ display: "flex", gap: spacing.lg }} aria-label="Link normativi">
          {accessibilityUrl ? (
            <a
              href={accessibilityUrl}
              style={{ color: colors.info, textDecoration: "underline" }}
              aria-label="Dichiarazione di accessibilità"
            >
              Accessibilità
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setViewerPage("accessibility")}
              style={navStyle}
              aria-label="Dichiarazione di accessibilità"
              aria-haspopup="dialog"
            >
              Accessibilità
            </button>
          )}
          {privacyUrl ? (
            <a
              href={privacyUrl}
              style={{ color: colors.info, textDecoration: "underline" }}
              aria-label="Privacy e Cookie Policy"
            >
              Privacy & Cookie
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setViewerPage("privacy")}
              style={navStyle}
              aria-label="Privacy e Cookie Policy"
              aria-haspopup="dialog"
            >
              Privacy & Cookie
            </button>
          )}
          {openSourceUrl ? (
            <a
              href={openSourceUrl}
              style={{ color: colors.info, textDecoration: "underline" }}
              aria-label="Open Source e licenze"
            >
              Open Source
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setViewerPage("open-source")}
              style={navStyle}
              aria-label="Open Source e licenze"
              aria-haspopup="dialog"
            >
              Open Source
            </button>
          )}
        </nav>
      </footer>

      <LegalPagesViewer page={viewerPage} onClose={closeViewer} />
    </>
  );
}
